import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

/**
 * Public-site language context.
 * - Languages: en (default), pt, es
 * - Persists choice to localStorage
 * - Components consume `useLanguage()` to read/set lang and `useT()` for static UI strings
 */

export const SUPPORTED_LANGUAGES = ["en", "pt", "es"];
export const DEFAULT_LANGUAGE = "en";
const STORAGE_KEY = "gdl_lang";

const LanguageContext = createContext({
  lang: DEFAULT_LANGUAGE,
  setLanguage: () => {},
});

const readStoredLang = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED_LANGUAGES.includes(v) ? v : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
};

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(readStoredLang);

  const setLanguage = useCallback((next) => {
    if (!SUPPORTED_LANGUAGES.includes(next)) return;
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  // Reflect lang on <html> so screen readers + downstream CSS hooks can react
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLanguage }), [lang, setLanguage]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}

/* ============================================================================
 * Static UI strings (chrome that's NOT in the CMS).
 * Anything that lives in the CMS (hero, services, method block, project text,
 * etc.) is delivered already-resolved by /api/public/bundle?lang=...
 * ========================================================================== */
const UI_STRINGS = {
  en: {
    "nav.home": "Home",
    "nav.portfolio": "Portfolio",
    "nav.services": "Services",
    "nav.about": "About",
    "nav.contact": "Contact",
    "nav.requestConsultation": "Request Consultation",
    "nav.menu": "Menu",
    "footer.navigate": "Navigate",
    "footer.privateChannel": "Private Channel",
    "hero.eyebrow": "An Emanuele Gomes Atelier",
    "hero.exploreCta": "Explore Portfolio",
    "services.eyebrow": "Pillars — Capabilities",
    "services.title.lead": "Four",
    "services.title.italic": "disciplines",
    "services.title.tail": "One atelier.",
    "services.intro": "Every engagement is a bespoke commission. Nothing templated, nothing outsourced. Each system is engineered to fit a single client's cadence — and scale silently behind their brand.",
    "portfolio.eyebrow": "Selected Commissions",
    "portfolio.title.lead": "A",
    "portfolio.title.italic": "private",
    "portfolio.title.tail": "portfolio.",
    "portfolio.viewAll": "View All",
    "portfolio.pageEyebrow": "Portfolio",
    "portfolio.pageTitle.lead": "A",
    "portfolio.pageTitle.italic": "private",
    "portfolio.pageTitle.tail": "archive of commissions.",
    "portfolio.pageSubtitle": "Each project begins with a quiet conversation and ends with an owned, one-of-one digital system.",
    "portfolio.empty": "No commissions yet.",
    "portfolio.filterAll": "All",
    "about.eyebrow": "About the Architect",
    "about.cta": "Schedule a Private Briefing",
    "contact.eyebrow": "A Private Enquiry",
    "contact.label.name": "Name",
    "contact.label.email": "Email",
    "contact.label.phone": "Phone (optional)",
    "contact.label.message": "Project Brief",
    "contact.sending": "Sending…",
    "contact.success": "Message received. A private response will follow.",
    "loading": "loading…",
    "nav.insights": "Insights",
    "footer.insights": "Insights",
    "insights.pageEyebrow": "Insights",
    "insights.pageTitle.lead": "An",
    "insights.pageTitle.italic": "editorial",
    "insights.pageTitle.tail": "archive.",
    "insights.pageSubtitle": "Notes on private digital systems, automation, and the architecture of operational excellence.",
    "insights.searchPlaceholder": "Search articles…",
    "insights.filterAll": "All",
    "insights.empty": "No articles yet.",
    "insights.readArticle": "Read article",
    "insights.featuredEyebrow": "Featured Insights",
    "insights.featuredTitle.lead": "Notes from",
    "insights.featuredTitle.italic": "the atelier",
    "insights.featuredTitle.tail": ".",
    "insights.viewAll": "All Insights",
    "insight.notFound": "Article not found",
    "insight.back": "Back to Insights",
    "insight.allInsights": "All Insights",
    "insight.byAuthor": "by",
    "insight.cta.title.lead": "Want a system",
    "insight.cta.title.italic": "like this",
    "insight.cta.title.tail": "?",
    "insight.cta.subtitle": "Begin a private conversation. Every commission starts with a quiet brief.",
    "insight.cta.button": "Start a Private Briefing",
    "project.notFound": "Not Found",
    "project.back": "Back to Portfolio",
    "project.allCommissions": "All Commissions",
    "project.meta.client": "Client",
    "project.meta.year": "Year",
    "project.meta.category": "Category",
    "project.meta.status": "Status",
    "project.meta.delivered": "Delivered",
    "project.meta.draft": "Draft",
    "project.meta.private": "Private",
    "project.theBrief": "The Brief",
    "project.highlights": "Highlights",
    "project.gallery": "Gallery",
    "project.film": "Film",
    "project.cta.title.lead": "Want something",
    "project.cta.title.italic": "like this",
    "project.cta.title.tail": "?",
    "project.cta.subtitle": "Every engagement begins with a quiet conversation. Share your brief and we'll respond personally.",
    "project.cta.fallbackButton": "Request Similar Project",
  },
  pt: {
    "nav.home": "Início",
    "nav.portfolio": "Portfólio",
    "nav.services": "Serviços",
    "nav.about": "Sobre",
    "nav.contact": "Contato",
    "nav.requestConsultation": "Solicitar Consultoria",
    "nav.menu": "Menu",
    "footer.navigate": "Navegar",
    "footer.privateChannel": "Canal Privado",
    "hero.eyebrow": "Um Atelier Emanuele Gomes",
    "hero.exploreCta": "Explorar Portfólio",
    "services.eyebrow": "Pilares — Competências",
    "services.title.lead": "Quatro",
    "services.title.italic": "disciplinas",
    "services.title.tail": "Um único atelier.",
    "services.intro": "Cada projeto é uma comissão sob medida. Nada de modelos, nada terceirizado. Cada sistema é projetado para se ajustar à cadência de um único cliente — e escalar silenciosamente sob sua marca.",
    "portfolio.eyebrow": "Comissões Selecionadas",
    "portfolio.title.lead": "Um portfólio",
    "portfolio.title.italic": "privado",
    "portfolio.title.tail": ".",
    "portfolio.viewAll": "Ver Tudo",
    "portfolio.pageEyebrow": "Portfólio",
    "portfolio.pageTitle.lead": "Um arquivo",
    "portfolio.pageTitle.italic": "privado",
    "portfolio.pageTitle.tail": "de comissões.",
    "portfolio.pageSubtitle": "Cada projeto começa com uma conversa discreta e termina com um sistema digital próprio e exclusivo.",
    "portfolio.empty": "Nenhuma comissão ainda.",
    "portfolio.filterAll": "Todos",
    "about.eyebrow": "Sobre o Arquiteto",
    "about.cta": "Agendar uma Reunião Privada",
    "contact.eyebrow": "Uma Solicitação Privada",
    "contact.label.name": "Nome",
    "contact.label.email": "E-mail",
    "contact.label.phone": "Telefone (opcional)",
    "contact.label.message": "Resumo do Projeto",
    "contact.sending": "Enviando…",
    "contact.success": "Mensagem recebida. Uma resposta privada será enviada.",
    "loading": "carregando…",
    "nav.insights": "Insights",
    "footer.insights": "Insights",
    "insights.pageEyebrow": "Insights",
    "insights.pageTitle.lead": "Um arquivo",
    "insights.pageTitle.italic": "editorial",
    "insights.pageTitle.tail": ".",
    "insights.pageSubtitle": "Notas sobre sistemas digitais privados, automação e a arquitetura da excelência operacional.",
    "insights.searchPlaceholder": "Buscar artigos…",
    "insights.filterAll": "Todos",
    "insights.empty": "Nenhum artigo ainda.",
    "insights.readArticle": "Ler artigo",
    "insights.featuredEyebrow": "Insights em Destaque",
    "insights.featuredTitle.lead": "Notas",
    "insights.featuredTitle.italic": "do atelier",
    "insights.featuredTitle.tail": ".",
    "insights.viewAll": "Todos os Insights",
    "insight.notFound": "Artigo não encontrado",
    "insight.back": "Voltar para Insights",
    "insight.allInsights": "Todos os Insights",
    "insight.byAuthor": "por",
    "insight.cta.title.lead": "Quer um sistema",
    "insight.cta.title.italic": "assim",
    "insight.cta.title.tail": "?",
    "insight.cta.subtitle": "Inicie uma conversa privada. Cada projeto começa com um briefing discreto.",
    "insight.cta.button": "Iniciar uma Reunião Privada",
    "project.notFound": "Não Encontrado",
    "project.back": "Voltar ao Portfólio",
    "project.allCommissions": "Todas as Comissões",
    "project.meta.client": "Cliente",
    "project.meta.year": "Ano",
    "project.meta.category": "Categoria",
    "project.meta.status": "Status",
    "project.meta.delivered": "Entregue",
    "project.meta.draft": "Rascunho",
    "project.meta.private": "Privado",
    "project.theBrief": "O Briefing",
    "project.highlights": "Destaques",
    "project.gallery": "Galeria",
    "project.film": "Vídeo",
    "project.cta.title.lead": "Quer algo",
    "project.cta.title.italic": "assim",
    "project.cta.title.tail": "?",
    "project.cta.subtitle": "Cada projeto começa com uma conversa discreta. Compartilhe seu briefing e responderemos pessoalmente.",
    "project.cta.fallbackButton": "Solicitar Projeto Similar",
  },
  es: {
    "nav.home": "Inicio",
    "nav.portfolio": "Portafolio",
    "nav.services": "Servicios",
    "nav.about": "Sobre",
    "nav.contact": "Contacto",
    "nav.requestConsultation": "Solicitar Consultoría",
    "nav.menu": "Menú",
    "footer.navigate": "Navegar",
    "footer.privateChannel": "Canal Privado",
    "hero.eyebrow": "Un Atelier Emanuele Gomes",
    "hero.exploreCta": "Explorar Portafolio",
    "services.eyebrow": "Pilares — Capacidades",
    "services.title.lead": "Cuatro",
    "services.title.italic": "disciplinas",
    "services.title.tail": "Un solo atelier.",
    "services.intro": "Cada proyecto es una comisión a medida. Nada de plantillas, nada subcontratado. Cada sistema está diseñado para ajustarse al ritmo de un único cliente — y escalar silenciosamente bajo su marca.",
    "portfolio.eyebrow": "Comisiones Seleccionadas",
    "portfolio.title.lead": "Un portafolio",
    "portfolio.title.italic": "privado",
    "portfolio.title.tail": ".",
    "portfolio.viewAll": "Ver Todo",
    "portfolio.pageEyebrow": "Portafolio",
    "portfolio.pageTitle.lead": "Un archivo",
    "portfolio.pageTitle.italic": "privado",
    "portfolio.pageTitle.tail": "de comisiones.",
    "portfolio.pageSubtitle": "Cada proyecto comienza con una conversación discreta y termina con un sistema digital propio y exclusivo.",
    "portfolio.empty": "Aún no hay comisiones.",
    "portfolio.filterAll": "Todos",
    "about.eyebrow": "Sobre el Arquitecto",
    "about.cta": "Agendar una Reunión Privada",
    "contact.eyebrow": "Una Consulta Privada",
    "contact.label.name": "Nombre",
    "contact.label.email": "Correo",
    "contact.label.phone": "Teléfono (opcional)",
    "contact.label.message": "Resumen del Proyecto",
    "contact.sending": "Enviando…",
    "contact.success": "Mensaje recibido. Una respuesta privada será enviada.",
    "loading": "cargando…",
    "nav.insights": "Insights",
    "footer.insights": "Insights",
    "insights.pageEyebrow": "Insights",
    "insights.pageTitle.lead": "Un archivo",
    "insights.pageTitle.italic": "editorial",
    "insights.pageTitle.tail": ".",
    "insights.pageSubtitle": "Notas sobre sistemas digitales privados, automatización y la arquitectura de la excelencia operacional.",
    "insights.searchPlaceholder": "Buscar artículos…",
    "insights.filterAll": "Todos",
    "insights.empty": "Aún no hay artículos.",
    "insights.readArticle": "Leer artículo",
    "insights.featuredEyebrow": "Insights Destacados",
    "insights.featuredTitle.lead": "Notas",
    "insights.featuredTitle.italic": "del atelier",
    "insights.featuredTitle.tail": ".",
    "insights.viewAll": "Todos los Insights",
    "insight.notFound": "Artículo no encontrado",
    "insight.back": "Volver a Insights",
    "insight.allInsights": "Todos los Insights",
    "insight.byAuthor": "por",
    "insight.cta.title.lead": "¿Quiere un sistema",
    "insight.cta.title.italic": "así",
    "insight.cta.title.tail": "?",
    "insight.cta.subtitle": "Comience una conversación privada. Cada proyecto inicia con un brief discreto.",
    "insight.cta.button": "Iniciar una Reunión Privada",
    "project.notFound": "No Encontrado",
    "project.back": "Volver al Portafolio",
    "project.allCommissions": "Todas las Comisiones",
    "project.meta.client": "Cliente",
    "project.meta.year": "Año",
    "project.meta.category": "Categoría",
    "project.meta.status": "Estado",
    "project.meta.delivered": "Entregado",
    "project.meta.draft": "Borrador",
    "project.meta.private": "Privado",
    "project.theBrief": "El Brief",
    "project.highlights": "Destacados",
    "project.gallery": "Galería",
    "project.film": "Video",
    "project.cta.title.lead": "¿Quiere algo",
    "project.cta.title.italic": "así",
    "project.cta.title.tail": "?",
    "project.cta.subtitle": "Cada proyecto comienza con una conversación discreta. Comparta su brief y responderemos personalmente.",
    "project.cta.fallbackButton": "Solicitar Proyecto Similar",
  },
};

export function useT() {
  const { lang } = useLanguage();
  return useCallback(
    (key) => {
      const pack = UI_STRINGS[lang] || UI_STRINGS[DEFAULT_LANGUAGE];
      return pack[key] ?? UI_STRINGS[DEFAULT_LANGUAGE][key] ?? key;
    },
    [lang]
  );
}
